import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import ChartRenderer from './ChartRenderer';
import type { ChartSpec } from '../types/chat.types';

interface HealthTrendParam {
  parameter: string;
  trend_value?: number | null;
  confidence?: number;
  latest_value?: number | null;
  mean_value?: number | null;
  unit?: string;
  chart_data?: Record<string, unknown>[];
  insufficient_data?: boolean;
  data_points?: number;
}

interface HealthTrendsPanelProps {
  healthTrends: {
    serial_number?: string;
    sections_analyzed?: string[];
    results?: Record<string, HealthTrendParam[]>;
  };
  compact?: boolean;
}

const getTrendIcon = (trendValue?: number | null) => {
  if (trendValue == null) return <Minus size={14} />;
  if (trendValue > 0) return <TrendingUp size={14} />;
  if (trendValue < 0) return <TrendingDown size={14} />;
  return <Minus size={14} />;
};

const getTrendColor = (trendValue?: number | null): 'success' | 'error' | 'default' => {
  if (trendValue == null) return 'default';
  if (trendValue > 0) return 'error';
  if (trendValue < 0) return 'success';
  return 'default';
};

const HealthTrendsPanel: React.FC<HealthTrendsPanelProps> = ({ healthTrends, compact = false }) => {
  const results = healthTrends.results || {};
  const sections = Object.entries(results);

  if (!sections.length) return null;

  // Build chart specs from health trend data
  const chartSpecs: ChartSpec[] = [];
  for (const [section, params] of sections) {
    for (const p of params) {
      if (p.insufficient_data || !p.chart_data?.length || p.chart_data.length < 2) continue;
      chartSpecs.push({
        chart_type: 'line',
        title: `${p.parameter} — ${section}`,
        x_axis: { key: 'date', label: 'Date' },
        y_axis: { key: 'value', label: p.parameter, unit: p.unit },
        series: [
          { key: 'value', label: p.parameter },
          { key: 'trend', label: 'Trend' },
        ],
        data: p.chart_data,
      });
    }
  }

  const maxCharts = compact ? 2 : chartSpecs.length;
  const chartHeight = compact ? 200 : 250;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Health Trends — {healthTrends.serial_number || 'Drone'}
      </Typography>

      {/* Summary chips */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
        {sections.map(([section, params]) =>
          params
            .filter((p) => !p.insufficient_data)
            .map((p) => (
              <Chip
                key={`${section}-${p.parameter}`}
                icon={getTrendIcon(p.trend_value)}
                label={`${p.parameter}: ${p.latest_value != null ? p.latest_value : '?'}${
                  p.unit || ''
                }`}
                size="small"
                color={getTrendColor(p.trend_value)}
                variant="outlined"
              />
            ))
        )}
      </Box>

      {/* Charts */}
      {chartSpecs.slice(0, maxCharts).map((spec, i) => (
        <ChartRenderer key={i} spec={spec} height={chartHeight} />
      ))}

      {compact && chartSpecs.length > maxCharts && (
        <Typography variant="caption" color="text.secondary">
          +{chartSpecs.length - maxCharts} more charts — expand to fullscreen
        </Typography>
      )}
    </Box>
  );
};

export default HealthTrendsPanel;
